// Cadence › Priorities (`/c/{slug}/cadence/priorities`; PRIORITIES-HOME, 06 §Cadence "Priorities:
// by owner seat, quarter, track state, milestones"): the quarter's priorities — the chart's
// Priorities (`goal.kind = priority`) — as a dense list, the core concept's top-level home.
//   - One read (`GET priorities?quarter=`, cut by the server to the Priorities audience) plus the
//     cached org chart (owners, teams, tree order). A seat whose priorities the caller may not
//     see is counted under the toolbar, never named.
//   - Grouped by owner (the company's own — the seats at the top of the chart — first, then
//     every seat in chart order) or by team; filters All / Mine / Needs attention; archived
//     priorities on request.
//   - Each row: the status (on track / at risk / off track / done; a menu for writers), the
//     title, the owner (team view), the milestone bar, the due date, and on hover Edit, Archive
//     and Delete. A row opens to its milestones: tick, add, remove. Every write is optimistic
//     (the cache first, the server's copy over it, a rollback and the toast on failure) and
//     refreshes the chart's Priorities lens.
//   - Writers (the seat's holder, managers on every seat: `writable_seat_ids`) add priorities
//     from the header or a seat's "+", and carry a quarter's unfinished priorities into the next.
//   - Each seat links to itself on the chart with the Priorities lens and its panel open; the
//     panel's "View all" comes back here filtered to that seat (`?seat=`).
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AvatarTile,
  Badge,
  Button,
  buttonVariants,
  Checkbox,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  InlineBar,
  Input,
  LensBandDot,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Segmented,
  Textarea,
  Toolbar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ArrowUpRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CornerDownRightIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  TargetIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { type FormEvent, type ReactNode, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import type { Goal, PriorityBoard } from "../../api/types";
import { modeAccess, rulesByKey } from "../../extensions/modes";
import { keepFocus } from "../../focus/focus";
import { NoAccessPage } from "../../pages/status-pages";
import { RaiseIssueButton } from "../cadence/issue-dialogs";
import { chartKeys, orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { seatHolderLabel } from "../format";
import { myMembershipId } from "../home/home-queries";
import { Loading } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";
import { useCompany, useSession } from "../use-feature";
import {
  dueLabel,
  type Group,
  groupBySeat,
  groupByTeam,
  milestonesDone,
  patchFor,
  quarterLabel,
  type SeatInfo,
  STATUS_BAND,
  STATUS_CHOICES,
  STATUS_LABELS,
  type Status,
  seatsInOrder,
  shiftQuarter,
  statusOf,
  summarise,
} from "./priorities-model";

/**
 * The page's gate: open to whoever may open Cadence or the chart (priorities are `core` and
 * every member reads them on the chart, while Cadence is a readers' mode behind `kpi`); the
 * server cuts the list to the Priorities audience either way. Cadence's mode root, full height
 * (like Chart's): the list scrolls under the header and toolbar.
 */
export function PrioritiesGate({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const company = useCompany();
  const ok = ["cadence", "chart"].some((key) => {
    const mode = rulesByKey(key);
    return mode ? modeAccess(mode, me, company) === "ok" : false;
  });
  if (!ok) return <NoAccessPage what="Priorities" slug={company.slug} />;
  return (
    <div data-mode-root="cadence" className="flex h-full min-h-0 flex-col">
      {children}
    </div>
  );
}

export type PrioritiesGroup = "owner" | "team";
export type PrioritiesFilter = "all" | "mine" | "attention";

export const prioritiesKey = (co: string, quarter: string) =>
  ["company", co, "priorities", quarter] as const;

export const prioritiesQuery = (api: Api, co: string, quarter: string | undefined) =>
  queryOptions({
    queryKey: prioritiesKey(co, quarter ?? "current"),
    queryFn: () =>
      api.client.priorities.list({ params: { co }, query: quarter ? { quarter } : {} }),
    staleTime: 30_000,
  });

type GoalPatchBody = Parameters<Api["client"]["goals"]["update"]>[0]["body"];

const AUDIENCE: Record<PriorityBoard["visibility"], string> = {
  everyone: "everyone in the company",
  reporting_line: "each seat's holder and the line above it",
  holder: "each seat's holder",
  managers: "managers",
};

/** The row grid: chevron, status, title, owner, milestones, due, status label, actions. */
const ROW_GRID =
  "grid grid-cols-[16px_16px_minmax(0,1fr)_auto_112px_64px_84px_100px] items-center gap-x-2.5 max-md:grid-cols-[16px_16px_minmax(0,1fr)_auto_64px_100px]";

export function PrioritiesScreen({
  api,
  company,
  quarter: quarterParam,
  group,
  filter,
  seat: seatFilter,
  onSearch,
}: {
  api: Api;
  company: CompanyAccess;
  quarter: string | undefined;
  group: PrioritiesGroup;
  filter: PrioritiesFilter;
  seat: string | undefined;
  onSearch: (next: {
    quarter?: string | undefined;
    group?: PrioritiesGroup;
    filter?: PrioritiesFilter;
    seat?: string | undefined;
  }) => void;
}) {
  const qc = useQueryClient();
  const { me } = useSession();
  const board = useQuery(prioritiesQuery(api, company.id, quarterParam));
  const chart = useQuery({ ...orgChartQuery(api, company.id), retry: false });
  const [archived, setArchived] = useState(false);
  const [composer, setComposer] = useState<{ seat: string | null } | null>(null);
  const [carrying, setCarrying] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const data = board.data;
  const quarter = data?.quarter ?? quarterParam ?? null;
  const key = prioritiesKey(company.id, quarterParam ?? "current");
  const seats = useMemo(() => seatsInOrder(chart.data), [chart.data]);
  const seatById = new Map(seats.map((s) => [s.id, s]));
  const writable = new Set(data?.writable_seat_ids ?? []);
  const me_ = me ? myMembershipId(me, company) : null;
  const mine = new Set(
    seats.filter((s) => s.holders.some((h) => h.membership_id === me_)).map((s) => s.id),
  );

  // Writes ---------------------------------------------------------------------------------
  const settle = () => [chartKeys.orgChart(company.id), ["company", company.id, "priorities"]];
  const patchCache = (fn: (goals: Goal[]) => Goal[]) =>
    qc.setQueryData<PriorityBoard>(key, (b) => (b ? { ...b, data: fn(b.data) } : b));
  const update = useOptimisticMutation({
    keys: () => [key],
    apply: (v: { goal: Goal; body: GoalPatchBody; next: Goal }) =>
      patchCache((gs) => gs.map((g) => (g.id === v.goal.id ? v.next : g))),
    mutationFn: (v) =>
      api.client.goals.update({ params: { co: company.id, id: v.goal.id }, body: v.body }),
    reconcile: (saved) => patchCache((gs) => gs.map((g) => (g.id === saved.id ? saved : g))),
    settle,
  });
  const create = useOptimisticMutation({
    keys: () => [key],
    apply: (v: { seat: string; title: string; due_at: string | null; milestones: string[] }) =>
      patchCache((gs) => [...gs, draftGoal(company.id, v, quarter ?? "")]),
    mutationFn: (v) =>
      api.client.goals.create({
        params: { co: company.id, id: v.seat },
        body: {
          title: v.title,
          ...(quarter ? { period_key: quarter } : {}),
          ...(v.due_at ? { due_at: v.due_at } : {}),
          milestones: v.milestones.map((t) => ({ title: t })),
        },
      }),
    reconcile: (saved) =>
      patchCache((gs) => [...gs.filter((g) => !g.id.startsWith("draft-")), saved]),
    settle,
  });
  const remove = useOptimisticMutation({
    keys: () => [key],
    apply: (g: Goal) => patchCache((gs) => gs.filter((x) => x.id !== g.id)),
    mutationFn: (g) => api.client.goals.delete({ params: { co: company.id, id: g.id } }),
    settle,
  });
  const carry = useOptimisticMutation({
    keys: () => [key],
    apply: () => {},
    mutationFn: (v: { from: string; to: string }) =>
      api.client.priorities.carryOver({ params: { co: company.id }, body: v }),
    settle,
    onSuccess: (r, v) => {
      setCarrying(false);
      setNotice(
        `Carried ${r.created.length} ${r.created.length === 1 ? "priority" : "priorities"} into ${quarterLabel(v.to)}${r.skipped ? ` (${r.skipped} already there)` : ""}.`,
      );
      onSearch({ quarter: v.to });
    },
  });
  const patch = (g: Goal, body: GoalPatchBody) => {
    const next: Goal = {
      ...g,
      ...(body.title !== undefined ? { title: body.title } : {}),
      ...(body.due_at !== undefined ? { due_at: body.due_at ?? null } : {}),
      ...(body.state !== undefined ? { state: body.state } : {}),
      ...(body.track !== undefined ? { track: body.track } : {}),
      ...(body.milestones !== undefined
        ? {
            milestones: body.milestones.map((m, position) => {
              const old = g.milestones.find((x) => x.id === m.id);
              return {
                ...(old ?? draftMilestone(company.id, g.id, m.title, position)),
                title: m.title,
                position,
                done_at: m.done
                  ? (old?.done_at ?? new Date().toISOString())
                  : m.done === false
                    ? null
                    : (old?.done_at ?? null),
              };
            }),
          }
        : {}),
    };
    update.mutate({ goal: g, body, next });
  };

  // What is shown --------------------------------------------------------------------------
  const all = data?.data ?? [];
  const live = all.filter((g) => statusOf(g) !== "archived");
  const shown = all
    .filter((g) => archived || statusOf(g) !== "archived")
    .filter((g) => !seatFilter || g.seat_id === seatFilter)
    .filter((g) => filter !== "mine" || mine.has(g.seat_id))
    .filter((g) => filter !== "attention" || ["at_risk", "off"].includes(statusOf(g)));
  const summary = summarise(seatFilter ? live.filter((g) => g.seat_id === seatFilter) : live);
  const archivedCount = all.length - live.length;
  const emptyWritable = new Set(
    filter === "all" && !seatFilter
      ? [...writable].filter((id) => mine.has(id) || composer?.seat === id)
      : seatFilter && writable.has(seatFilter)
        ? [seatFilter]
        : [],
  );
  const groups: Group[] =
    group === "team"
      ? groupByTeam(shown, chart.data, seats)
      : groupBySeat(shown, seats, emptyWritable);
  const openCarry = live.filter((g) => g.state === "open" && writable.has(g.seat_id));
  const target = quarter ? shiftQuarter(quarter, 1) : null;
  const year = Number((quarter ?? "").slice(0, 4)) || new Date().getUTCFullYear();
  const filterSeat = seatFilter ? seatById.get(seatFilter) : undefined;

  const quarters = data?.quarters ?? [];
  const pickQuarter = (q: string) => {
    setNotice(null);
    onSearch({ quarter: q === data?.current_quarter ? undefined : q });
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="priorities">
      <LocationHeader
        icon={TargetIcon}
        title="Priorities"
        badge={
          quarter ? (
            <Badge variant={quarter === data?.current_quarter ? "accent" : "neutral"}>
              {quarterLabel(quarter)}
              {quarter === data?.current_quarter ? " · this quarter" : ""}
            </Badge>
          ) : null
        }
        actions={
          <>
            <QuarterSwitch quarter={quarter} quarters={quarters} onPick={pickQuarter} />
            {openCarry.length > 0 && target ? (
              <Button variant="ghost" size="sm" onClick={() => setCarrying(true)}>
                <CornerDownRightIcon data-icon="inline-start" aria-hidden="true" />
                <span className="max-md:sr-only">Carry over unfinished</span>
              </Button>
            ) : null}
            {writable.size > 0 ? (
              <Button
                size="sm"
                onClick={() =>
                  setComposer({
                    seat:
                      seatFilter && writable.has(seatFilter)
                        ? seatFilter
                        : ([...mine].find((id) => writable.has(id)) ?? null),
                  })
                }
              >
                <PlusIcon data-icon="inline-start" aria-hidden="true" />
                <span className="max-sm:sr-only">Add priority</span>
              </Button>
            ) : null}
          </>
        }
      />
      <Toolbar bleed aria-label="Priorities view">
        <Segmented
          size="sm"
          aria-label="Group by"
          value={group}
          onValueChange={(g) => onSearch({ group: g })}
          options={[
            { value: "owner", label: "By owner" },
            { value: "team", label: "By team" },
          ]}
        />
        <Segmented
          size="sm"
          aria-label="Show"
          value={filter}
          onValueChange={(f) => onSearch({ filter: f })}
          options={[
            { value: "all", label: "All" },
            { value: "mine", label: "Mine" },
            { value: "attention", label: "Needs attention" },
          ]}
        />
        {filterSeat ? (
          <Badge variant="accent" className="pr-1">
            {seatHolderLabel(filterSeat.title, filterSeat.holders[0]?.name)}
            <button
              type="button"
              aria-label="Show every seat"
              className="rounded-xs hover:bg-primary/10"
              onClick={() => onSearch({ seat: undefined })}
            >
              <XIcon aria-hidden="true" className="size-3" />
            </button>
          </Badge>
        ) : null}
        <SummaryLine summary={summary} />
        {archivedCount > 0 ? (
          <Button
            variant="ghost"
            size="xs"
            aria-pressed={archived}
            className="shrink-0 aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
            onClick={() => setArchived((a) => !a)}
          >
            <ArchiveIcon data-icon="inline-start" aria-hidden="true" />
            Archived {archivedCount}
          </Button>
        ) : null}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-10 max-md:px-4">
        {notice ? (
          <Notice tone="success" className="mt-3" dismissible onDismiss={() => setNotice(null)}>
            {notice}
          </Notice>
        ) : null}
        {data && data.hidden_seats > 0 ? (
          <p
            className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"
            data-slot="hidden-seats"
          >
            <LockIcon aria-hidden="true" className="size-3.5" />
            {data.hidden_seats} {data.hidden_seats === 1 ? "seat's" : "seats'"} priorities are not
            shown to you: priorities are shown to {AUDIENCE[data.visibility]}.
          </p>
        ) : null}
        {composer ? (
          <Composer
            seats={seats.filter((s) => writable.has(s.id))}
            seat={composer.seat}
            quarter={quarter}
            onCancel={() => setComposer(null)}
            onAdd={(v) => {
              create.mutate(v);
              setComposer(null);
            }}
          />
        ) : null}
        {board.isPending ? (
          <div className="pt-4">
            <Loading label="Loading priorities" rows={6} />
          </div>
        ) : board.isError ? (
          <div className="pt-4">
            <ErrorNotice what="Priorities" error={board.error} />
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={TargetIcon}
            className="mt-10"
            title={
              all.length
                ? "Nothing matches this view"
                : `No priorities for ${quarter ? quarterLabel(quarter) : "this quarter"} yet`
            }
            description={
              all.length
                ? "Switch to All, or show the archived ones."
                : "A priority is what a seat commits to deliver this quarter, with milestones to get there. It shows on the chart's Priorities lens."
            }
            actions={
              writable.size > 0 && !all.length ? (
                <Button size="sm" onClick={() => setComposer({ seat: [...mine][0] ?? null })}>
                  <PlusIcon data-icon="inline-start" aria-hidden="true" />
                  Add priority
                </Button>
              ) : undefined
            }
          />
        ) : (
          groups.map((g) => (
            <GroupBlock
              key={g.key}
              group={g}
              company={company}
              byTeam={group === "team"}
              seatById={seatById}
              writable={writable}
              year={year}
              onAdd={(seat) => setComposer({ seat })}
              onPatch={patch}
              onDelete={(goal) => remove.mutate(goal)}
            />
          ))
        )}
      </div>

      <AlertDialog open={carrying} onOpenChange={(o) => !carry.isPending && setCarrying(o)}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Carry {openCarry.length} unfinished{" "}
              {openCarry.length === 1 ? "priority" : "priorities"} into{" "}
              {target ? quarterLabel(target) : "the next quarter"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Each open priority{writable.size < seats.length ? " on your seats" : ""} is copied
              into {target ? quarterLabel(target) : "the next quarter"} with the milestones not yet
              done, and closed in {quarter ? quarterLabel(quarter) : "this quarter"} as missed. One
              already there by the same title is skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={carry.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={carry.isPending || !quarter || !target}
              onClick={() => quarter && target && carry.mutate({ from: quarter, to: target })}
            >
              Carry over
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QuarterSwitch({
  quarter,
  quarters,
  onPick,
}: {
  quarter: string | null;
  quarters: readonly string[];
  onPick: (q: string) => void;
}) {
  const id = useId();
  if (!quarter) return null;
  const prev = shiftQuarter(quarter, -1);
  const next = shiftQuarter(quarter, 1);
  const options = [...new Set([...quarters, quarter])].sort().reverse();
  return (
    <span className="flex items-center gap-0.5" data-slot="quarter-switch">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Previous quarter, ${quarterLabel(prev)}`}
        onClick={() => onPick(prev)}
      >
        <ChevronLeftIcon aria-hidden="true" />
      </Button>
      <label htmlFor={id} className="sr-only">
        Quarter
      </label>
      <NativeSelect
        id={id}
        size="sm"
        value={quarter}
        onChange={(e) => onPick(e.target.value)}
        className="w-28"
      >
        {options.map((q) => (
          <NativeSelectOption key={q} value={q}>
            {quarterLabel(q)}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Next quarter, ${quarterLabel(next)}`}
        onClick={() => onPick(next)}
      >
        <ChevronRightIcon aria-hidden="true" />
      </Button>
    </span>
  );
}

function SummaryLine({ summary: s }: { summary: ReturnType<typeof summarise> }) {
  const parts: [Status, number][] = (
    [
      ["on", s.on],
      ["at_risk", s.at_risk],
      ["off", s.off],
      ["done", s.done],
      ["missed", s.missed],
    ] as [Status, number][]
  ).filter(([, n]) => n > 0);
  return (
    <span
      className="ml-auto flex shrink-0 items-center gap-3 text-xs text-muted-foreground tabular-nums"
      data-slot="priorities-summary"
    >
      <span className="text-secondary">
        {s.total} {s.total === 1 ? "priority" : "priorities"}
      </span>
      {parts.map(([k, n]) => (
        <span key={k} className="inline-flex items-center gap-1.5 max-lg:hidden">
          <StatusDot status={k} decorative />
          {n} {STATUS_LABELS[k].toLowerCase()}
        </span>
      ))}
      {s.milestones.total > 0 ? (
        <span className="max-md:hidden">
          Milestones {s.milestones.done}/{s.milestones.total}
        </span>
      ) : null}
    </span>
  );
}

/** The status's dot; `decorative` when its label is already written beside it. */
function StatusDot({ status, decorative = false }: { status: Status; decorative?: boolean }) {
  return (
    <LensBandDot
      band={STATUS_BAND[status]}
      aria-label={STATUS_LABELS[status]}
      {...(decorative ? { "aria-hidden": true, role: "presentation" } : {})}
      className={cn(status === "done" && "ring-2 ring-success-soft")}
    />
  );
}

const STATUS_BADGE: Record<Status, "success" | "warning" | "danger" | "accent" | "neutral"> = {
  on: "success",
  at_risk: "warning",
  off: "danger",
  done: "accent",
  missed: "neutral",
  archived: "neutral",
};

function GroupBlock({
  group,
  company,
  byTeam,
  seatById,
  writable,
  year,
  onAdd,
  onPatch,
  onDelete,
}: {
  group: Group;
  company: CompanyAccess;
  byTeam: boolean;
  seatById: Map<string, SeatInfo>;
  writable: Set<string>;
  year: number;
  onAdd: (seat: string) => void;
  onPatch: (g: Goal, body: GoalPatchBody) => void;
  onDelete: (g: Goal) => void;
}) {
  const seat = group.seat;
  const holder = seat?.holders[0];
  const done = group.goals.reduce((n, g) => n + milestonesDone(g), 0);
  const total = group.goals.reduce((n, g) => n + g.milestones.length, 0);
  return (
    <section
      className="mt-4"
      data-slot="priority-group"
      data-kind={group.kind}
      aria-label={group.kind === "company" ? `Company priorities` : group.label}
    >
      <div className="sticky top-0 z-sticky flex h-9 items-center gap-2 border-b border-border bg-background text-sm">
        {seat ? (
          <AvatarTile
            name={holder?.name ?? seat.title}
            kind={holder?.kind === "digital_employee" ? "agent" : "person"}
            size="sm"
          />
        ) : null}
        <h2 className="min-w-0 truncate font-semibold text-foreground">
          {group.kind === "company" ? "Company" : group.label}
        </h2>
        {seat && group.kind === "company" ? (
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {seatHolderLabel(seat.title, holder?.name)}
          </span>
        ) : null}
        <span className="text-xs text-faint tabular-nums">{group.goals.length}</span>
        {total > 0 ? (
          <span className="ml-1 flex w-24 items-center gap-1.5 max-sm:hidden">
            <InlineBar value={(done / total) * 100} size="xs" aria-hidden="true" />
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-0.5">
          {seat ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Link
                    to="/c/$slug/kpis/chart"
                    params={{ slug: company.slug }}
                    search={(prev: Record<string, unknown>) => ({
                      ...keepFocus(prev),
                      lens: "keystones" as const,
                      seat: seat.id,
                    })}
                    aria-label={`Open ${seat.title} on the chart`}
                    className={buttonVariants({ variant: "ghost", size: "icon-xs" })}
                  />
                }
              >
                <ArrowUpRightIcon aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>Open on the chart</TooltipContent>
            </Tooltip>
          ) : null}
          {seat && writable.has(seat.id) ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Add a priority to ${seat.title}`}
                    onClick={() => onAdd(seat.id)}
                  />
                }
              >
                <PlusIcon aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>Add a priority</TooltipContent>
            </Tooltip>
          ) : null}
        </span>
      </div>
      {group.goals.length ? (
        <ul>
          {group.goals.map((g) => (
            <PriorityRow
              key={g.id}
              goal={g}
              owner={byTeam ? seatById.get(g.seat_id) : undefined}
              canWrite={writable.has(g.seat_id) && !g.id.startsWith("draft-")}
              year={year}
              onPatch={onPatch}
              onDelete={onDelete}
            />
          ))}
        </ul>
      ) : (
        <p className="py-2 pl-[42px] text-xs text-faint">No priorities this quarter.</p>
      )}
    </section>
  );
}

function PriorityRow({
  goal: g,
  owner,
  canWrite,
  year,
  onPatch,
  onDelete,
}: {
  goal: Goal;
  owner: SeatInfo | undefined;
  canWrite: boolean;
  year: number;
  onPatch: (g: Goal, body: GoalPatchBody) => void;
  onDelete: (g: Goal) => void;
}) {
  const { api } = useRouter().options.context;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const status = statusOf(g);
  const done = milestonesDone(g);
  const total = g.milestones.length;
  const due = dueLabel(g.due_at, year);
  const closed = status === "done" || status === "missed" || status === "archived";
  const listId = useId();

  return (
    <li data-slot="priority-row" data-status={status} className="border-b border-subtle">
      {editing ? (
        <EditRow
          goal={g}
          onCancel={() => setEditing(false)}
          onSave={(body) => {
            onPatch(g, body);
            setEditing(false);
          }}
        />
      ) : (
        <div className={cn(ROW_GRID, "group/row h-9 pr-1 hover:bg-sunken")}>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={listId}
            aria-label={`${open ? "Hide" : "Show"} the milestones of ${g.title}`}
            onClick={() => setOpen((o) => !o)}
            className="flex size-4 items-center justify-center rounded-xs text-faint hover:text-foreground"
          >
            <ChevronRightIcon
              aria-hidden="true"
              className={cn("size-3.5 transition-transform", open && "rotate-90")}
            />
          </button>
          {canWrite ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button
                    type="button"
                    aria-label={`Status of ${g.title}: ${STATUS_LABELS[status]}`}
                    className="flex size-4 items-center justify-center rounded-full hover:ring-2 hover:ring-border"
                  />
                }
              >
                <StatusDot status={status} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-36">
                {STATUS_CHOICES.map((s) => (
                  <DropdownMenuItem
                    key={s}
                    aria-current={s === status ? "true" : undefined}
                    onClick={() => s !== status && onPatch(g, patchFor(s))}
                  >
                    <StatusDot status={s} decorative />
                    {STATUS_LABELS[s]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="flex size-4 items-center justify-center">
              <StatusDot status={status} />
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            onDoubleClick={() => canWrite && setEditing(true)}
            className={cn(
              "min-w-0 truncate text-left text-sm",
              closed ? "text-muted-foreground" : "text-foreground",
              status === "done" && "line-through decoration-faint",
            )}
            title={g.title}
          >
            {g.title}
          </button>
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            {owner ? (
              <>
                <AvatarTile
                  name={owner.holders[0]?.name ?? owner.title}
                  kind={owner.holders[0]?.kind === "digital_employee" ? "agent" : "person"}
                  size="xs"
                />
                <span className="max-w-56 truncate max-md:hidden">
                  {seatHolderLabel(owner.title, owner.holders[0]?.name)}
                </span>
              </>
            ) : null}
          </span>
          <span
            className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums max-md:hidden"
            data-slot="milestone-progress"
          >
            {total > 0 ? (
              <>
                <InlineBar
                  value={(done / total) * 100}
                  size="xs"
                  tone={status === "done" ? "success" : "primary"}
                  className="w-16"
                  aria-hidden="true"
                />
                <span>
                  {done}/{total}
                  <span className="sr-only"> milestones done</span>
                </span>
              </>
            ) : (
              <span className="text-faint">No milestones</span>
            )}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums" data-slot="due">
            {due ?? <span className="text-faint">—</span>}
          </span>
          <span className="max-md:hidden">
            <Badge variant={STATUS_BADGE[status]}>{STATUS_LABELS[status]}</Badge>
          </span>
          <span className="flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100">
            {/* MEETINGS: an off-track or at-risk priority is raised as an issue. */}
            {(status === "off" || status === "at_risk") && !g.id.startsWith("draft-") ? (
              <RaiseIssueButton
                api={api}
                co={g.company_id}
                label={`Raise ${g.title} as an issue`}
                className="text-faint hover:text-foreground"
                defaults={{
                  title: `${g.title}: ${STATUS_LABELS[status].toLowerCase()}`,
                  owner_seat_id: g.seat_id,
                  priority: status === "off" ? 1 : 2,
                  source: {
                    kind: "goal",
                    id: g.id,
                    label: `Priority · ${STATUS_LABELS[status]}`,
                  },
                }}
              />
            ) : null}
            {canWrite ? (
              <>
                <IconAction label={`Edit ${g.title}`} tip="Edit" onClick={() => setEditing(true)}>
                  <PencilIcon aria-hidden="true" />
                </IconAction>
                {status === "archived" ? (
                  <IconAction
                    label={`Restore ${g.title}`}
                    tip="Restore"
                    onClick={() => onPatch(g, { state: "open" })}
                  >
                    <ArchiveRestoreIcon aria-hidden="true" />
                  </IconAction>
                ) : (
                  <IconAction
                    label={`Archive ${g.title}`}
                    tip="Archive"
                    onClick={() => onPatch(g, { state: "dropped" })}
                  >
                    <ArchiveIcon aria-hidden="true" />
                  </IconAction>
                )}
                <IconAction
                  label={`Delete ${g.title}`}
                  tip="Delete"
                  danger
                  onClick={() => setDeleting(true)}
                >
                  <Trash2Icon aria-hidden="true" />
                </IconAction>
              </>
            ) : null}
          </span>
        </div>
      )}
      {open ? (
        <Milestones
          id={listId}
          goal={g}
          canWrite={canWrite}
          year={year}
          onWrite={(ms) => onPatch(g, { milestones: ms })}
        />
      ) : null}
      <AlertDialog open={deleting} onOpenChange={setDeleting}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{g.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The priority and its {total} {total === 1 ? "milestone" : "milestones"} are removed
              for good. Archive it instead to keep the record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setDeleting(false);
                onDelete(g);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

function IconAction({
  label,
  tip,
  danger = false,
  onClick,
  children,
}: {
  label: string;
  tip: string;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            onClick={onClick}
            className={cn(
              "text-faint",
              danger ? "hover:text-danger-text" : "hover:text-foreground",
            )}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

type MilestoneWrite = { id?: string; title: string; done?: boolean };

function Milestones({
  id,
  goal: g,
  canWrite,
  year,
  onWrite,
}: {
  id: string;
  goal: Goal;
  canWrite: boolean;
  year: number;
  onWrite: (ms: MilestoneWrite[]) => void;
}) {
  const [text, setText] = useState("");
  const base = (): MilestoneWrite[] =>
    g.milestones.map((m) => ({ id: m.id, title: m.title, done: m.done_at !== null }));
  const add = (e: FormEvent) => {
    e.preventDefault();
    const title = text.trim();
    if (!title) return;
    onWrite([...base(), { title }]);
    setText("");
  };
  return (
    <ul id={id} className="pb-2 pl-[52px]" data-slot="milestones">
      {g.milestones.map((m) => {
        const due = dueLabel(m.due_at, year);
        return (
          <li key={m.id} className="group/ms flex h-7 min-w-0 items-center gap-2 text-sm">
            <Checkbox
              checked={m.done_at !== null}
              disabled={!canWrite || m.id.startsWith("draft-")}
              onCheckedChange={(on) =>
                onWrite(base().map((x) => (x.id === m.id ? { ...x, done: on === true } : x)))
              }
              aria-label={`${m.title}: ${m.done_at ? "done" : "not done"}`}
            />
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                m.done_at ? "text-faint line-through" : "text-secondary",
              )}
            >
              {m.title}
            </span>
            {due ? <span className="text-xs text-muted-foreground tabular-nums">{due}</span> : null}
            {canWrite ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove the milestone ${m.title}`}
                className="text-faint opacity-0 group-hover/ms:opacity-100 focus-visible:opacity-100 hover:text-danger-text pointer-coarse:opacity-100"
                onClick={() => onWrite(base().filter((x) => x.id !== m.id))}
              >
                <XIcon aria-hidden="true" />
              </Button>
            ) : null}
          </li>
        );
      })}
      {!g.milestones.length && !canWrite ? (
        <li className="h-7 text-xs leading-7 text-faint">No milestones.</li>
      ) : null}
      {canWrite ? (
        <li>
          <form onSubmit={add} className="flex h-8 items-center gap-2">
            <PlusIcon aria-hidden="true" className="size-4 text-faint" />
            <Input
              aria-label={`Add a milestone to ${g.title}`}
              placeholder="Add a milestone, Enter to save"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="h-7 max-w-96 border-transparent bg-transparent px-1 shadow-none hover:border-border focus-visible:border-border"
            />
          </form>
        </li>
      ) : null}
    </ul>
  );
}

const dateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : "");
const dueAtOf = (day: string) => (day ? `${day}T17:00:00.000Z` : null);

function EditRow({
  goal: g,
  onSave,
  onCancel,
}: {
  goal: Goal;
  onSave: (body: GoalPatchBody) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(g.title);
  const [due, setDue] = useState(dateInput(g.due_at));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    onSave({
      ...(t !== g.title ? { title: t } : {}),
      ...(due !== dateInput(g.due_at) ? { due_at: dueAtOf(due) } : {}),
    });
  };
  return (
    <form
      onSubmit={submit}
      onKeyDown={(e) => e.key === "Escape" && onCancel()}
      className="flex min-h-11 flex-wrap items-center gap-2 py-1.5 pl-[52px]"
      data-slot="edit-priority"
    >
      <Input
        aria-label="Priority"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
        className="h-8 min-w-60 flex-1"
      />
      <Input
        type="date"
        aria-label="Due date"
        value={due}
        onChange={(e) => setDue(e.target.value)}
        className="h-8 w-36"
      />
      <Button type="submit" size="sm" disabled={!title.trim()}>
        Save
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

function Composer({
  seats,
  seat,
  quarter,
  onAdd,
  onCancel,
}: {
  seats: SeatInfo[];
  seat: string | null;
  quarter: string | null;
  onAdd: (v: { seat: string; title: string; due_at: string | null; milestones: string[] }) => void;
  onCancel: () => void;
}) {
  const [owner, setOwner] = useState(seat ?? seats[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [milestones, setMilestones] = useState("");
  const ownerId = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!owner || !title.trim()) return;
    onAdd({
      seat: owner,
      title: title.trim(),
      due_at: dueAtOf(due),
      milestones: milestones
        .split("\n")
        .map((t) => t.trim())
        .filter(Boolean),
    });
  };
  return (
    <form
      onSubmit={submit}
      onKeyDown={(e) => e.key === "Escape" && onCancel()}
      className="mt-4 flex flex-col gap-2 rounded-md border border-border bg-card p-3 shadow-xs"
      data-slot="add-priority"
      aria-label="New priority"
    >
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={ownerId} className="sr-only">
          Owner seat
        </label>
        <NativeSelect
          id={ownerId}
          size="sm"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          className="w-56"
        >
          {seats.map((s) => (
            <NativeSelectOption key={s.id} value={s.id}>
              {seatHolderLabel(s.title, s.holders[0]?.name)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <Input
          aria-label="Priority"
          placeholder={`What this seat delivers in ${quarter ? quarterLabel(quarter) : "the quarter"}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          className="h-8 min-w-64 flex-1"
        />
        <Input
          type="date"
          aria-label="Due date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="h-8 w-36"
        />
      </div>
      <Textarea
        aria-label="Milestones, one per line"
        placeholder="Milestones, one per line (optional)"
        value={milestones}
        onChange={(e) => setMilestones(e.target.value)}
        rows={2}
        className="min-h-14 text-sm"
      />
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={!owner || !title.trim()}>
          Add priority
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <span className="ml-auto text-xs text-faint">Shows on the chart's Priorities lens.</span>
      </div>
    </form>
  );
}

/** The optimistic row of a priority being added (replaced by the server's copy). */
function draftGoal(
  co: string,
  v: { seat: string; title: string; due_at: string | null; milestones: string[] },
  quarter: string,
): Goal {
  const at = new Date().toISOString();
  const id = `draft-${Math.random().toString(36).slice(2)}`;
  return {
    company_id: co,
    id,
    kind: "priority",
    seat_id: v.seat,
    team_id: null,
    title: v.title,
    due_at: v.due_at,
    period_key: quarter,
    metric_def_id: null,
    target: null,
    state: "open",
    track: "on",
    flagged_to_portfolio: false,
    portfolio_link_ref: null,
    vision_page_id: null,
    actor_kind: "person",
    actor_membership_id: null,
    created_at: at,
    updated_at: at,
    milestones: v.milestones.map((t, i) => draftMilestone(co, id, t, i)),
  };
}

function draftMilestone(co: string, goalId: string, title: string, position: number) {
  const at = new Date().toISOString();
  return {
    company_id: co,
    id: `draft-${goalId}-${position}`,
    goal_id: goalId,
    title,
    due_at: null,
    done_at: null,
    position,
    actor_kind: "person" as const,
    actor_membership_id: null,
    created_at: at,
    updated_at: at,
  };
}
